import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApiMode } from '../context/ApiModeContext';
import './DashboardLayout.css';

const logoUrl = process.env.PUBLIC_URL + '/connect-lane-logo.png';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLive, setMode } = useApiMode();
  const userName = localStorage.getItem('userName') || 'Viren Shah';
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    navigate('/login');
  };

  const masterDataChildren = [
    { path: '/master-data/countries', label: 'Countries' },
    { path: '/master-data/exchange-rate', label: 'Exchange Rate' },
    { path: '/master-data/purpose-codes', label: 'Purpose Codes' },
    { path: '/master-data/payment-methods', label: 'Payment Methods' },
  ];

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/partners', label: 'Partners', icon: '🤝' },
    { path: '/master-data', label: 'Master Data', icon: '📋', children: masterDataChildren },
    { path: '/customers', label: 'Customers', icon: '👥' },
    { path: '/beneficiaries', label: 'Beneficiaries', icon: '👤' },
    { path: '/bank-accounts', label: 'Bank Account', icon: '🏦' },
    { path: '/customer-documents', label: 'Customer Documents', icon: '📄' },
    { path: '/payment-modes', label: 'Payment Modes', icon: '💸' },
    { path: '/receiver-mode', label: 'Receiver Mode', icon: '📨' },
    { path: '/payments', label: 'Transactions', icon: '💳' },
    { path: '/edd-rules', label: 'EDD Rules', icon: '📜' },
    { path: '/kyc', label: 'KYC', icon: '🆔' },
    { path: '/compliance', label: 'Compliance', icon: '🛡️' },
    { path: '/settings', label: 'Settings', icon: '⚙' },
  ];

  // Convert emoji icons to professional SVG-like text icons
  const getIcon = (icon) => {
    const iconMap = {
      '📊': '▦',  // Dashboard - grid
      '🤝': '◉',   // Partners - circle
      '👥': '◯',   // Customers - users
      '👤': '◎',   // Beneficiaries - person
      '🏦': '◉',   // Bank Account
      '📄': '▤',   // Customer Documents
      '💳': '◈',   // Payments - diamond
      '📋': '▤',   // Master Data - grid
      '💸': '$',   // Payment Modes - dollar
      '📨': '◊',   // Receiver Mode - lozenge
      '📜': '▣',   // EDD Rules - square
      '🆔': '◐',   // KYC - half circle
      '🛡️': '◙',   // Compliance - shield
      '⚙': '⚙',   // Settings - gear
    };
    return iconMap[icon] || icon;
  };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={logoUrl} alt="Connect Lane" className="sidebar-logo-img" />
            <span className="logo-text"><span className="logo-connect">Connect</span> <span className="logo-lane">Lane</span></span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            item.children ? (
              <div key={item.path} className="nav-group">
                <div
                  className={`nav-item nav-item-parent ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
                  onClick={() => !location.pathname.startsWith(item.path) && navigate(item.children[0].path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && !location.pathname.startsWith(item.path) && navigate(item.children[0].path)}
                >
                  <span className="nav-icon">{getIcon(item.icon)}</span>
                  <span className="nav-label">{item.label}</span>
                  <span className="nav-expand">{location.pathname.startsWith(item.path) ? '▼' : '▶'}</span>
                </div>
                {location.pathname.startsWith(item.path) && (
                  <div className="nav-submenu">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`nav-item nav-item-child ${location.pathname === child.path ? 'active' : ''}`}
                      >
                        {location.pathname === child.path && <div className="active-indicator"></div>}
                        <span className="nav-label">{child.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                {location.pathname === item.path && <div className="active-indicator"></div>}
                <span className="nav-icon">{getIcon(item.icon)}</span>
                <span className="nav-label">{item.label}</span>
              </Link>
            )
          ))}
        </nav>
      </aside>

      <div className="dashboard-content">
        <header className="dashboard-header">
          <div className="header-content">
            <h1 className="dashboard-header-title">{(() => {
              const path = location.pathname;
              const hideTitlePaths = ['/partners', '/customers', '/beneficiaries', '/bank-accounts', '/payment-modes', '/receiver-mode', '/edd-rules', '/kyc', '/compliance'];
              if (path.startsWith('/master-data/')) {
                const sub = masterDataChildren.find(c => c.path === path);
                return sub ? sub.label : 'Master Data';
              }
              if (hideTitlePaths.includes(path)) return '';
              const item = menuItems.find(i => i.path === path && !i.children);
              return item ? item.label : (path === '/dashboard' ? 'Dashboard' : '');
            })()}</h1>
            <div className="header-actions">
              <div className="header-mode-switch">
                <button
                  type="button"
                  className={`header-mode-btn ${!isLive ? 'active' : ''}`}
                  onClick={() => setMode('sandbox')}
                >
                  Sandbox
                </button>
                <button
                  type="button"
                  className={`header-mode-btn ${isLive ? 'active' : ''}`}
                  onClick={() => setMode('live')}
                >
                  Live
                </button>
              </div>
              <div className="user-menu-container" ref={menuRef}>
                <button 
                  className="user-menu-btn"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                >
                  <div className="user-avatar">VS</div>
                  <div className="user-info">
                    <div className="user-name">{userName}</div>
                  </div>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {showProfileMenu && (
                  <div className="profile-dropdown">
                    <button className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                      Profile
                    </button>
                    <button className="dropdown-item" onClick={() => setShowProfileMenu(false)}>
                      Notifications
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowProfileMenu(false); navigate('/settings'); }}>
                      Settings
                    </button>
                    <button className="dropdown-item" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
